
import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SystemBootProps {
    onComplete?: () => void;
    isReady?: boolean;
    businessName?: string;
    userName?: string;
    isFirstBoot?: boolean;
}

/* ── Brand tokens — theme variables, so the boot screen follows the theme ── */
const B = {
    bg: 'var(--fd-base)',
    surface: 'var(--fd-surface-2)',
    champagne: 'var(--fd-text-bright)',
    text: 'var(--fd-text)',
    textMuted: 'var(--fd-text-soft)',
    amber: 'var(--fd-gold)',
    amberGlow: 'color-mix(in oklab, var(--fd-gold-fill) 45%, transparent)',
    amberSoft: 'color-mix(in oklab, var(--fd-gold-fill) 25%, transparent)',
    amberSubtle: 'color-mix(in oklab, var(--fd-gold-fill) 8%, transparent)',
    green: 'var(--fd-success)',
    greenDim: 'color-mix(in oklab, var(--fd-success) 25%, transparent)',
    greenSubtle: 'color-mix(in oklab, var(--fd-success) 8%, transparent)',
};

/* ── Boot Message Sequences ──────────────────────────────────────── */
const FIRST_BOOT_SEQUENCE = [
    "FIRING UP A FRESH FORGE...",
    "LAYING THE FOUNDATION...",
    "FOUNDATION SET ✓",
    "CONFIGURING YOUR WORKSPACE...",
    "WORKSPACE READY ✓",
    "PREPARING YOUR TOOLKIT...",
    "TOOLS SHARPENED ✓",
];

const RETURNING_BOOT_SEQUENCE = [
    "WARMING UP THE FORGE...",
    "HEATING THE PIPES...",
    "PIPES HEATED ✓",
    "IGNITING TAX ENGINE...",
    "ENGINE RUNNING ✓",
    "LOADING YOUR WORKSHOP...",
    "WORKSHOP RESTORED ✓",
];

/* ── Step timing: confirmations (✓) are slightly snappier ────────── */
const getStepDuration = (msg: string) => msg.includes('✓') ? 700 : 900;

/* ── Sentient Greeting Pools ─────────────────────────────────────── */
function getTimeOfDay(): string {
    const h = new Date().getHours();
    if (h < 12) return 'MORNING';
    if (h < 17) return 'AFTERNOON';
    return 'EVENING';
}

function pickSentientGreeting(name: string, isFirst: boolean): string {
    const n = name.toUpperCase();
    const tod = getTimeOfDay();

    const firstPool = [
        `EVERYTHING'S SET, ${n}. LET'S BUILD.`,
        `YOUR FORGE AWAITS, ${n}.`,
        `FIRST DAY. BEST DAY. LET'S GO.`,
        `ALL YOURS, ${n}. MAKE IT COUNT.`,
    ];

    const returningPool = [
        `WELCOME BACK, ${n}. THE FORGE MISSED YOU.`,
        `AH, ${n}. RIGHT ON TIME.`,
        `GOOD ${tod}, ${n}. LET'S GET TO WORK.`,
        `${n}'S FORGE. LOCKED AND LOADED.`,
        `SYSTEMS PRIMED. OVER TO YOU, ${n}.`,
        `THE PIPES ARE HOT. GO TIME.`,
        `ALL CYLINDERS FIRING. YOUR MOVE, ${n}.`,
    ];

    const pool = isFirst ? firstPool : returningPool;
    return pool[Math.floor(Math.random() * pool.length)];
}

/* ── Orbital Ring SVG Component ──────────────────────────────────── */
function OrbitalRing({ size, duration, tiltX, opacity, dashArray }: {
    size: number; duration: number; tiltX: number; opacity: number; dashArray: string;
}) {
    return (
        <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ perspective: '600px' }}
        >
            <motion.div
                animate={{ rotateZ: 360 }}
                transition={{ duration, repeat: Infinity, ease: 'linear' }}
                style={{
                    width: size,
                    height: size,
                    transformStyle: 'preserve-3d',
                    transform: `rotateX(${tiltX}deg)`,
                }}
            >
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={size / 2 - 2}
                        stroke={B.green}
                        strokeWidth="1"
                        strokeDasharray={dashArray}
                        opacity={opacity}
                    />
                </svg>
            </motion.div>
        </div>
    );
}

/* ── Hex Neural Web with Synapse Firing ──────────────────────────── */
interface NodeDef { x: number; y: number }
interface ConnDef { x1: number; y1: number; x2: number; y2: number; from: number; to: number }

function HexWeb({ progress }: { progress: number }) {
    const [activeSynapses, setActiveSynapses] = useState<Set<number>>(new Set());
    const [activeNodes, setActiveNodes] = useState<Set<number>>(new Set());

    // Generate hexagonal node positions
    const nodes: NodeDef[] = useMemo(() => {
        const pts: NodeDef[] = [];
        const cx = 200, cy = 200;
        const rings = [
            { r: 60, count: 6, offset: 0 },
            { r: 110, count: 8, offset: Math.PI / 8 },
            { r: 155, count: 12, offset: 0 },
        ];
        pts.push({ x: cx, y: cy });
        rings.forEach(({ r, count, offset }) => {
            for (let i = 0; i < count; i++) {
                const angle = (2 * Math.PI / count) * i + offset;
                pts.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
            }
        });
        return pts;
    }, []);

    // Generate connections with node indices
    const connections: ConnDef[] = useMemo(() => {
        const conns: ConnDef[] = [];
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const dx = nodes[i].x - nodes[j].x;
                const dy = nodes[i].y - nodes[j].y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 75) {
                    conns.push({
                        x1: nodes[i].x, y1: nodes[i].y,
                        x2: nodes[j].x, y2: nodes[j].y,
                        from: i, to: j,
                    });
                }
            }
        }
        return conns;
    }, [nodes]);

    // Synapse firing: randomly light up connections every 400–800ms
    useEffect(() => {
        const fire = () => {
            const visibleConns = Math.floor(connections.length * Math.min(progress * 1.2, 1));
            if (visibleConns === 0) return;

            // Pick 2-4 random connections to fire
            const count = 2 + Math.floor(Math.random() * 3);
            const newSynapses = new Set<number>();
            const newNodes = new Set<number>();

            for (let i = 0; i < count; i++) {
                const idx = Math.floor(Math.random() * visibleConns);
                newSynapses.add(idx);
                newNodes.add(connections[idx].from);
                newNodes.add(connections[idx].to);
            }

            setActiveSynapses(newSynapses);
            setActiveNodes(newNodes);

            // Clear after the glow fades
            setTimeout(() => {
                setActiveSynapses(new Set());
                setActiveNodes(new Set());
            }, 500);
        };

        const interval = setInterval(fire, 600 + Math.random() * 400);
        fire(); // Initial fire
        return () => clearInterval(interval);
    }, [connections, progress]);

    const visibleNodes = Math.floor(nodes.length * Math.min(progress * 1.3, 1));
    const visibleConns = Math.floor(connections.length * Math.min(progress * 1.2, 1));

    return (
        <svg
            width="400" height="400" viewBox="0 0 400 400"
            className="absolute"
            style={{ opacity: 0.2 + progress * 0.5 }}
        >
            {/* SVG filter for synapse glow */}
            <defs>
                <filter id="synapse-glow" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <filter id="node-glow" x="-150%" y="-150%" width="400%" height="400%">
                    <feGaussianBlur stdDeviation="8" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="blur" />
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Connections */}
            {connections.slice(0, visibleConns).map((c, i) => {
                const isFiring = activeSynapses.has(i);
                return (
                    <motion.line
                        key={`c-${i}`}
                        x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
                        stroke={isFiring ? B.amber : B.green}
                        strokeWidth={isFiring ? 3 : 0.5}
                        filter={isFiring ? 'url(#synapse-glow)' : undefined}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isFiring ? 0.9 : 0.3 }}
                        transition={{ duration: isFiring ? 0.15 : 0.3 }}
                    />
                );
            })}

            {/* Nodes */}
            {nodes.slice(0, visibleNodes).map((n, i) => {
                const isFiring = activeNodes.has(i);
                const isCenter = i === 0;
                return (
                    <motion.circle
                        key={`n-${i}`}
                        cx={n.x} cy={n.y}
                        r={isCenter ? 5 : isFiring ? 4.5 : 1.5}
                        fill={isFiring ? B.amber : isCenter ? B.amber : B.green}
                        filter={isFiring ? 'url(#node-glow)' : undefined}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                            opacity: isFiring ? 1 : isCenter ? 0.8 : 0.4,
                            scale: 1,
                        }}
                        transition={{ duration: isFiring ? 0.1 : 0.3 }}
                    />
                );
            })}

            {/* Synapse fire pulse — expanding ring at fired nodes */}
            {Array.from(activeNodes).filter(idx => idx < visibleNodes).map(idx => (
                <motion.circle
                    key={`pulse-${idx}`}
                    cx={nodes[idx].x} cy={nodes[idx].y}
                    r={2}
                    fill="none"
                    stroke={B.amber}
                    strokeWidth={1.5}
                    filter='url(#synapse-glow)'
                    initial={{ r: 3, opacity: 0.9 }}
                    animate={{ r: 22, opacity: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                />
            ))}
        </svg>
    );
}

/* ── Forge Spark Particles (memoized configs) ────────────────────── */
function SparkParticles() {
    // useState's lazy initialiser, not useMemo.
    //
    // useMemo's callback runs DURING RENDER, and under StrictMode or a concurrent
    // re-render React may run it more than once — so `Math.random()` here produced
    // a different set of sparks on some renders, restarting every animation
    // mid-flight. useMemo is also explicitly not a semantic guarantee: React may
    // discard a memo and recompute it. useState's initialiser runs exactly once
    // for the life of the component, which is what "randomise once" needs.
    const [sparks] = useState(() =>
        Array.from({ length: 15 }, (_, i) => ({
            w: Math.random() * 2 + 1,
            left: 10 + Math.random() * 80,
            bg: i % 3 === 0 ? B.amber : B.green,
            yEnd: -(200 + Math.random() * 300),
            xEnd: (Math.random() - 0.5) * 40,
            dur: 3 + Math.random() * 4,
            delay: Math.random() * 5,
        })));

    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {sparks.map((s, i) => (
                <motion.div
                    key={`spark-${i}`}
                    className="absolute rounded-full"
                    style={{
                        width: s.w,
                        height: s.w,
                        left: `${s.left}%`,
                        background: s.bg,
                        opacity: 0,
                    }}
                    animate={{
                        y: [0, s.yEnd],
                        opacity: [0, 0.6, 0],
                        x: [0, s.xEnd],
                    }}
                    transition={{
                        duration: s.dur,
                        repeat: Infinity,
                        delay: s.delay,
                        ease: 'easeOut',
                    }}
                />
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   SYSTEM BOOT — The Forge Brain
   ═══════════════════════════════════════════════════════════════════ */
export function SystemBoot({
    onComplete,
    isReady = true,
    businessName,
    userName,
    isFirstBoot = false,
}: SystemBootProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [exiting, setExiting] = useState(false);

    const sequence = isFirstBoot ? FIRST_BOOT_SEQUENCE : RETURNING_BOOT_SEQUENCE;

    // Pick sentient greeting once per mount — uses userName (personal), falls back to businessName
    const greetingName = userName || businessName || 'OPERATOR';
    const sentientGreeting = useMemo(
        () => pickSentientGreeting(greetingName, isFirstBoot),
        [greetingName, isFirstBoot]
    );

    // Full sequence including the sentient final message
    const fullSequence = useMemo(
        () => [...sequence, sentientGreeting],
        [sequence, sentientGreeting]
    );

    const progress = (currentStep + 1) / fullSequence.length;

    useEffect(() => {
        if (currentStep < fullSequence.length - 1) {
            const dur = getStepDuration(fullSequence[currentStep]);
            const timer = setTimeout(() => setCurrentStep(prev => prev + 1), dur);
            return () => clearTimeout(timer);
        } else {
            // At final (sentient) step — hold longer so it reads, then exit
            if (isReady && onComplete) {
                const timer = setTimeout(() => {
                    setExiting(true);
                    // Let exit animation play, then complete
                    setTimeout(() => onComplete(), 700);
                }, 1800);
                return () => clearTimeout(timer);
            }
        }
    }, [currentStep, isReady, onComplete, fullSequence]);

    // Dynamic font size based on name length
    const nameLength = businessName?.length || 0;
    const fontSizeClass = nameLength > 15
        ? 'text-2xl md:text-4xl'
        : nameLength > 8
            ? 'text-4xl md:text-5xl'
            : 'text-5xl md:text-6xl';

    return (
        <motion.div
            className="fd-fullscreen fixed inset-0 z-[100] flex items-center justify-center overflow-hidden select-none cursor-wait"
            style={{ background: B.bg, fontFamily: "'DM Sans', system-ui, sans-serif" }}
            animate={exiting ? { scale: 1.05, opacity: 0 } : { scale: 1, opacity: 1 }}
            transition={exiting ? { duration: 0.7, ease: 'easeOut' } : {}}
        >
            {/* ── Background Atmosphere ───────────────────────────────── */}
            {/* Warm ambient glow */}
            <div
                className="absolute inset-0"
                style={{
                    background: `
                        radial-gradient(ellipse 800px 600px at 50% 45%, ${B.amberSubtle} 0%, transparent 70%),
                        radial-gradient(ellipse 400px 400px at 50% 50%, ${B.greenSubtle} 0%, transparent 60%)
                    `,
                }}
            />

            {/* Grain texture overlay */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    opacity: 0.35,
                    mixBlendMode: 'overlay',
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='220' height='220' filter='url(%23n)' opacity='.035'/></svg>")`,
                }}
            />

            {/* Warm vignette */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: `radial-gradient(circle at center, transparent 40%, ${B.bg} 100%)`,
                }}
            />



            {/* ── Forge Spark Particles ──────────────────────────────── */}
            <SparkParticles />

            {/* ── Central Forge Brain ────────────────────────────────── */}
            <div className="relative z-10 flex flex-col items-center gap-6 md:gap-10 w-full max-w-lg p-6">

                {/* Brain layout box — smaller on mobile, full on desktop */}
                <div className="relative w-[266px] h-[266px] md:w-[380px] md:h-[380px]">
                    {/* Inner: fixed 380px canvas, CSS-scaled to fit the layout box */}
                    <div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-[0.7] md:scale-100"
                        style={{ width: 380, height: 380 }}
                    >
                        {/* Orbital Ring 1 — slow, wide */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <OrbitalRing size={380} duration={18} tiltX={65} opacity={0.15} dashArray="8 12" />
                        </div>
                        {/* Orbital Ring 2 — medium */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <OrbitalRing size={300} duration={12} tiltX={72} opacity={0.25} dashArray="4 8" />
                        </div>
                        {/* Orbital Ring 3 — fast, tight */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <OrbitalRing size={220} duration={8} tiltX={60} opacity={0.35} dashArray="2 6" />
                        </div>

                        {/* Hex Neural Web with Synapse Firing */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <HexWeb progress={progress} />
                        </div>

                        {/* Inner glow ring — intensifies with progress */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <motion.div
                                className="rounded-full"
                                style={{
                                    width: 160,
                                    height: 160,
                                    border: `1px solid color-mix(in oklab, var(--fd-gold-fill) ${(0.1 + progress * 0.35) * 100}%, transparent)`,
                                    boxShadow: `
                                        0 0 ${25 + progress * 40}px color-mix(in oklab, var(--fd-gold-fill) ${(0.06 + progress * 0.2) * 100}%, transparent),
                                        inset 0 0 ${15 + progress * 25}px color-mix(in oklab, var(--fd-gold-fill) ${(0.04 + progress * 0.1) * 100}%, transparent)
                                    `,
                                }}
                                animate={{ scale: [0.98, 1.02, 0.98] }}
                                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                            />
                        </div>

                        {/* Central Business Name (garage name) */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, ease: 'easeOut' }}
                            >
                                <div
                                    className={`font-black tracking-tighter text-center uppercase leading-tight ${fontSizeClass}`}
                                    style={{
                                        color: B.champagne,
                                        textShadow: `0 0 40px ${B.amberGlow}, 0 0 80px ${B.amberSoft}`,
                                        fontFamily: "'Syne', 'DM Sans', sans-serif",
                                    }}
                                >
                                    {businessName || "FDS"}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* ── Status Text Sequence ───────────────────────────── */}
                <div className="w-full h-8 flex items-center justify-center">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="font-bold tracking-widest text-xs md:text-sm text-center"
                            style={{
                                color: currentStep === fullSequence.length - 1
                                    ? B.champagne
                                    : B.text,
                                textShadow: currentStep === fullSequence.length - 1
                                    ? `0 0 20px ${B.amberGlow}`
                                    : `0 0 12px ${B.amberSoft}`,
                                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                            }}
                        >
                            <span
                                className="mr-2 inline-block"
                                style={{ color: B.amber }}
                            >
                                ›
                            </span>
                            {fullSequence[currentStep]}
                            {/* Blinking cursor — hidden on final message */}
                            {currentStep < fullSequence.length - 1 && (
                                <motion.span
                                    animate={{ opacity: [1, 0] }}
                                    transition={{ duration: 0.4, repeat: Infinity }}
                                    className="ml-1 inline-block w-2 h-3.5 align-middle"
                                    style={{ background: B.amber }}
                                />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* ── Enhanced Progress Bar ──────────────────────────── */}
                <div className="w-64 h-1 rounded-full overflow-hidden relative" style={{ background: B.amberSubtle }}>
                    {/* Segment marks */}
                    <div className="absolute inset-0 flex">
                        {fullSequence.slice(1).map((_, i) => (
                            <div
                                key={`seg-${i}`}
                                className="h-full"
                                style={{
                                    flex: 1,
                                    borderRight: i < fullSequence.length - 2
                                        ? `1px solid ${B.amberSubtle}`
                                        : 'none',
                                }}
                            />
                        ))}
                    </div>
                    {/* Fill */}
                    <motion.div
                        className="h-full relative"
                        style={{
                            background: B.green,
                            boxShadow: `0 0 12px ${B.greenDim}, 0 0 4px var(--fd-success)`,
                        }}
                        initial={{ width: '0%' }}
                        animate={{ width: `${progress * 100}%` }}
                        transition={{
                            duration: getStepDuration(fullSequence[currentStep]) / 1000,
                            ease: 'linear',
                        }}
                    >
                        {/* Leading edge shimmer */}
                        <div
                            className="absolute right-0 top-0 h-full w-3"
                            style={{
                                background: `linear-gradient(90deg, transparent, ${B.amber})`,
                                filter: 'blur(1px)',
                            }}
                        />
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
}
